import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Input } from '@components/Input';
import { ExternalLink } from '@components/Link';
import { Switch } from '@components/Switch';
import Tooltip from '@components/Tooltip';
import { Typography } from '@components/Typography';
import TeamSelect from '@features/TeamSelect';
import ChannelLocationSelect from '@features/ChannelLocationSelect';
import EmailDomainRecords from '@features/ChannelsForms/EmailDomainRecords';
import { useCopyToClipboard, useThrottle, useTranslation } from '@hooks';
import { DEFAULT_RETURN_PATH_SUBDOMAIN, isHostedEmailChannel } from '@constants/channels';
import { ExternalRoutes } from '@constants/routes';

const Container = styled('div')({
    display: 'grid',
    gridGap: '16px 24px',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    '@media (max-width: 600px)': {
        gridTemplateColumns: '100%',
        gridGap: '16px',
    },
    '& .team-select': {
        gridColumn: '1 / span 2',
    },
});

const FullRow = styled('div')({
    gridColumn: '1 / -1',
});

const ReturnPathRow = styled(FullRow)({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
});

const domainOf = email => ((email || '').split('@')[1] || '').toLowerCase();

export const returnPathPayload = values => (values.customReturnPath ? values.returnPathSubdomain : null);

const HostedEmailForm = ({ isEdit = false, domain = null, onDomainUpdated, selectedChannel }) => {
    const { t, i18n } = useTranslation();
    const { onCopy } = useCopyToClipboard();
    const [onCopyClick] = useThrottle(value => onCopy(value), 1000);
    const { control, watch, setValue } = useFormContext();
    const channels = useSelector(state => state.channels.data);

    const email = watch('email');
    const customReturnPath = watch('customReturnPath');
    const returnPathSubdomain = watch('returnPathSubdomain');
    const enableIncomingEmail = !!watch('enableIncomingEmail');
    const domainName = domain?.domain || domainOf(email);
    const connectedDomain =
        !isEdit && domainName
            ? channels.some(c => isHostedEmailChannel(c) && domainOf(c.emailParameters?.email) === domainName)
            : false;

    useEffect(() => {
        if (connectedDomain) {
            setValue('customReturnPath', false, { shouldValidate: true });
        }
    }, [connectedDomain, setValue]);

    const onCustomReturnPathChange = event => {
        setValue('customReturnPath', event.target.checked, { shouldValidate: true });
        if (event.target.checked) {
            setValue('returnPathSubdomain', returnPathSubdomain || DEFAULT_RETURN_PATH_SUBDOMAIN, {
                shouldValidate: true,
            });
        }
    };

    return (
        <Container>
            <Controller
                control={control}
                name="name"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('SettingsChannels.channelName')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : t('SettingsChannels.fromNameHint')}
                        required
                    />
                )}
            />
            <Controller
                control={control}
                name="email"
                render={({ field, fieldState: { error }, ref }) => {
                    const input = (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('SettingsChannels.emailAddress')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                            disabled={isEdit}
                        />
                    );
                    return isEdit ? (
                        <Tooltip title={t('SettingsChannels.emailAddressLocked')} placement="top">
                            <div>{input}</div>
                        </Tooltip>
                    ) : (
                        input
                    );
                }}
            />
            <TeamSelect data={selectedChannel} />
            {connectedDomain ? (
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)', alignSelf: 'center' }}>
                    {t('SettingsChannels.returnPathSubdomainReused', { domain: domainName })}
                </Typography>
            ) : null}
            {!isEdit && !connectedDomain ? (
                <ReturnPathRow>
                    <Switch
                        checked={!!customReturnPath}
                        handleChange={onCustomReturnPathChange}
                        label={t('SettingsChannels.customReturnPath')}
                    />
                    {customReturnPath ? (
                        <>
                            <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                                {t('SettingsChannels.customReturnPathHint')}
                            </Typography>
                            <Controller
                                control={control}
                                name="returnPathSubdomain"
                                render={({ field, fieldState: { error }, ref }) => (
                                    <Input
                                        ref={ref}
                                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                        label={t('SettingsChannels.returnPathSubdomain')}
                                        addonComponent={domainName ? `.${domainName}` : null}
                                        addonWithoutWrapper
                                        fullWidth
                                        error={!!error}
                                        helperText={error ? t(error.message) : null}
                                        required
                                    />
                                )}
                            />
                        </>
                    ) : null}
                </ReturnPathRow>
            ) : null}
            <ChannelLocationSelect data={selectedChannel} />
            {isEdit && domain ? (
                <FullRow>
                    <EmailDomainRecords
                        domain={domain}
                        channelId={selectedChannel?.id}
                        onDomainUpdated={onDomainUpdated}
                        collapseWhenVerified
                    />
                </FullRow>
            ) : null}
            <FullRow>
                <Switch
                    checked={enableIncomingEmail}
                    handleChange={event =>
                        setValue('enableIncomingEmail', event.target.checked, {
                            shouldDirty: true,
                            shouldValidate: true,
                        })
                    }
                    label={t('SettingsChannels.receiveEmailsInInbox')}
                />
            </FullRow>
            {enableIncomingEmail && selectedChannel?.emailIntakeAddress ? (
                <FullRow>
                    <Input
                        inputProps={{ value: selectedChannel.emailIntakeAddress }}
                        onAddonClick={() => onCopyClick(selectedChannel.emailIntakeAddress)}
                        addonComponent={<ContentCopyIcon sx={{ fontSize: 18, color: 'var(--text-secondary)' }} />}
                        label={t('SettingsChannels.forwardingAddress')}
                        fullWidth
                        disabled
                    />
                    <Typography
                        variant="caption"
                        sx={{ color: 'var(--text-secondary)', display: 'block', margin: '-12px 12px 0' }}
                    >
                        {t('SettingsChannels.receiveRepliesHint', { email: selectedChannel.emailParameters?.email })}{' '}
                        <ExternalLink href={ExternalRoutes.LearnMore[i18n.language].emailReplies} underline="hover">
                            {t('common.learnMore')}
                        </ExternalLink>
                    </Typography>
                </FullRow>
            ) : null}
        </Container>
    );
};

export default HostedEmailForm;
